type Block = {
  h: string;
  p?: string[];
  list?: string[];
  after?: string;
  subs?: { h: string; p: string }[];
};

const intro = [
  "At My Value Academy (MVA), a skill-development and learning platform by Shree Sati Group, we are committed to protecting the privacy and personal information of our students, learners, professionals, corporate clients, and website visitors. This Privacy Policy explains how we collect, use, store, protect, and disclose information through our website and related services.",
  "By accessing or using the website and its services, you acknowledge that you have read and understood this Privacy Policy and agree to the practices described below.",
];

const sections: Block[] = [
  {
    h: "1. Information We Collect",
    p: ["To provide our educational, training, certification, internship, and related services, we may collect certain personal information from you, including but not limited to:"],
    list: [
      "Full name",
      "Mobile number and email address",
      "Residential or correspondence address",
      "City and other relevant location information",
      "Date of birth or age, where required",
      "Educational and professional details",
      "Course or programme preferences",
      "Information provided through enquiry, registration, or admission forms",
      "Identification or documentation details, where required for a specific programme or service",
      "Payment and transaction-related information",
      "Any other information voluntarily provided by you while interacting with us",
    ],
    after: "The information collected may vary depending on the course, programme, service, enquiry, or transaction you choose.",
  },
  {
    h: "2. Information You Provide",
    p: ["You are responsible for providing accurate, complete, and up-to-date information when submitting an enquiry, registering for a course, applying for a programme, or using any of our service. Providing incorrect, incomplete, or outdated information may affect our ability to provide the requested services, and we shall not be responsible for consequences arising from inaccurate information supplied by you."],
  },
  {
    h: "3. Automatically Collected Information",
    p: ["When you visit or interact with our website, certain technical and usage information may be collected automatically. This may include:"],
    list: [
      "IP address",
      "Browser type and version",
      "Operating system",
      "Device information",
      "Website pages visited",
      "Date and time of access",
      "Referring website or source",
      "General website usage and interaction data",
    ],
    after: "We may use cookies, web beacons, pixels, analytics tools, and similar technologies to understand website usage, improve functionality, and enhance your experience. You may be able to control or disable cookies through your browser settings; however, doing so may affect certain website functionalities.",
  },
  {
    h: "4. How We Use Your Information",
    subs: [
      { h: "a. Providing Educational Services", p: "We may use your information to process enquiries, registrations, admissions, course enrolments, certifications, internships, workshops, corporate training programmes, and other services offered by us." },
      { h: "b. Communication", p: "We may use your contact information to communicate with you regarding course and programme enquiries, admissions and registrations, class or batch-related information, course updates and announcements, internship and placement-related information, customer or learner support, important administrative communications, events and training opportunities, and promotional and marketing communications, where permitted." },
      { h: "c. Personalisation and Service Improvement", p: "Your information may be used to understand your interests, preferences, and requirements so that we can improve our courses, programmes, learning experiences, website functionality, and related services." },
      { h: "d. Analytics and Website Improvement", p: "We may analyse website traffic, user behaviour, and interaction patterns to improve our website, content, digital experience, educational offerings, and communication." },
      { h: "e. Legal and Regulatory Compliance", p: "We may collect, use, retain, or disclose information where necessary to comply with applicable laws, regulations, legal proceedings, governmental requirements, or lawful requests, or to protect our legal rights and interests." },
    ],
  },
  {
    h: "5. Data Retention",
    p: [
      "We retain personal information only for as long as reasonably necessary to fulfil the purposes for which it was collected, provide our services, maintain appropriate records, resolve disputes, comply with legal or regulatory requirements, and enforce our agreements. You may request access to or correction of certain personal information held by us, subject to applicable laws and regulatory requirements. Where legally permitted, you may also request deletion of your personal information. Certain information may need to be retained where required for legal, regulatory, contractual, accounting, or legitimate business purposes.",
    ],
  },
  {
    h: "6. Sharing of Information with Third Parties",
    subs: [
      { h: "a. Service Providers and Educational Partners", p: "Depending on the service selected by you, information may be shared with relevant educational institutions, certification bodies, training partners, internship providers, placement partners, technology providers, payment processors, or other service providers. Such information will generally be shared only to the extent necessary to facilitate the requested service or fulfil a legitimate business or legal purpose. We are not responsible for the independent privacy practices, policies, or services of third-party organisations." },
      { h: "b. Legal Requirements", p: "We may disclose information where required or permitted by applicable law, regulation, court order, governmental authority, or legal process. Information may also be disclosed where reasonably necessary to prevent fraud, investigate misuse of our services, protect the rights, safety, property, or security of My Value Academy, our users, or other parties, or to establish, exercise, or defend legal claims." },
      { h: "c. Business Transfers", p: "If My Value Academy, Shree Sati Group, or any relevant business or assets undergoes a merger, acquisition, restructuring, sale, transfer, or similar corporate transaction, personal information may be transferred as part of that transaction, subject to applicable laws and the terms of this Privacy Policy." },
    ],
  },
  {
    h: "7. Data Security",
    p: ["We take reasonable and appropriate measures to safeguard the personal information entrusted to us against unauthorised access, alteration, disclosure, misuse, or destruction. However, no method of transmission over the internet or method of electronic storage is completely secure. Therefore, while we take reasonable steps to protect your information, we cannot guarantee absolute security of information transmitted to or stored by us."],
  },
  {
    h: "8. Third-Party Websites and Links",
    p: ["Our website may contain links to third-party websites, platforms, educational institutions, service providers, or other external resources. These third-party websites operate independently and may have their own privacy policies and terms of use. We do not control or take responsibility for the privacy practices, content, or security of such third-party websites."],
  },
  {
    h: "9. Marketing Communications",
    p: ["Where permitted by applicable law, we may contact you through email, telephone, SMS, WhatsApp, or other communication channels regarding courses, programmes, workshops, events, educational opportunities, promotional offers, and relevant updates. By opting in, you agree to receive promotional messages, offers, and updates from us and/or Shree Sati Group, and you may withdraw your consent at any time. Please note that essential service-related or transactional communications may continue where necessary."],
  },
  {
    h: "10. Children's Privacy",
    p: ["Our services are intended to be used by individuals who meet the applicable eligibility requirements of the relevant course or programme. We do not knowingly collect personal information from children in violation of applicable laws. Where a programme requires information relating to a minor, such information should be provided by or with the involvement of a parent or legal guardian, where required by law."],
  },
  {
    h: "11. Your Rights",
    p: ["Subject to applicable laws and regulatory requirements, you may have rights regarding your personal information, including the right to:"],
    list: [
      "Request access to personal information held by us",
      "Request correction of inaccurate or incomplete information",
      "Request deletion of information where legally permissible",
      "Withdraw consent where processing is based on consent",
      "Opt out of certain promotional communications",
    ],
    after: "Requests may be subject to verification and applicable legal or regulatory limitations.",
  },
  {
    h: "12. Changes to This Privacy Policy",
    p: ["We may update or modify this Privacy Policy from time to time to reflect changes in our services, business practices, technology, or applicable laws and regulations. Any updated version will be published on this page with the revised effective date, where applicable. We encourage you to review this Privacy Policy periodically."],
  },
  {
    h: "13. Contact Us",
    p: [
      "If you have any questions, concerns, requests, or complaints regarding this Privacy Policy or the handling of your personal information, please contact My Value Academy through the contact details provided on our website.",
      "By accessing and using the website and services, you acknowledge and agree to the practices described in this Privacy Policy.",
    ],
  },
];

const body = "text-[15px] leading-relaxed text-ink-soft";

export function PrivacyContent() {
  return (
    <div className="space-y-8">
      {intro.map((t) => (
        <p key={t} className={body}>{t}</p>
      ))}
      {sections.map((s) => (
        <div key={s.h}>
          <h2 className="font-display text-xl font-semibold text-ink">{s.h}</h2>
          {s.p?.map((t) => (
            <p key={t} className={`mt-2 ${body}`}>{t}</p>
          ))}
          {s.list && (
            <ul className={`mt-2 list-disc space-y-1 pl-5 ${body}`}>
              {s.list.map((i) => (
                <li key={i}>{i}</li>
              ))}
            </ul>
          )}
          {s.after && <p className={`mt-2 ${body}`}>{s.after}</p>}
          {s.subs?.map((x) => (
            <div key={x.h} className="mt-4">
              <h3 className="font-display text-base font-semibold text-ink">{x.h}</h3>
              <p className={`mt-1 ${body}`}>{x.p}</p>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
